import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, PanInfo } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import type { PreviewPage } from '../data/volume4Preview';
type PreviewLightboxProps = {
  pages: PreviewPage[];
  index: number | null;
  onClose: () => void;
  onChange: (index: number) => void;
};
const EASE = [0.23, 1, 0.32, 1] as const;
export function PreviewLightbox({
  pages,
  index,
  onClose,
  onChange
}: PreviewLightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [direction, setDirection] = useState(0);
  const open = index !== null;
  const go = useCallback((delta: number) => {
    if (index === null) return;
    const target = index + delta;
    if (target < 0 || target >= pages.length) return;
    setDirection(delta);
    onChange(target);
  }, [index, pages.length, onChange]);
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();else if (e.key === 'ArrowRight') go(1);else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, go, onClose]);
  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.x < -60 || info.velocity.x < -400) go(1);else if (info.offset.x > 60 || info.velocity.x > 400) go(-1);
  };
  const page = index !== null ? pages[index] : null;
  return <AnimatePresence data-id="element-208" data-mp="2klet4h31rb10">
      {open && page && <motion.div role="dialog" aria-modal="true" aria-label={`Volume 4 preview, page ${index + 1} of ${pages.length}`} className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/[0.92]" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} onClick={onClose} data-id="element-209" data-mp="2fppqg4vqde0h">
          <div className="absolute inset-x-0 top-0 flex items-center justify-between px-4 py-4 sm:px-6" data-id="element-210" data-mp="90p73ikwy0iv">
            <span className="rounded-full bg-white/10 px-3 py-1 text-sm font-semibold tabular-nums text-white" aria-live="polite" data-id="element-211" data-mp="wnx65irr0dcg">
              {index + 1} / {pages.length}
            </span>
            <button ref={closeRef} type="button" onClick={onClose} aria-label="Close preview" className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-shuttle" data-id="element-212" data-mp="rqdv0rqckgoq">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-213" data-mp="1pker3w8ligy4" />
            </button>
          </div>

          <button type="button" onClick={e => {
        e.stopPropagation();
        go(-1);
      }} disabled={index === 0} aria-label="Previous page" className="absolute left-4 top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/20 disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-shuttle sm:flex" data-id="element-214" data-mp="23k7ebhkwyf1c">
            <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-215" data-mp="1fu3hbt28ddjb" />
          </button>
          <button type="button" onClick={e => {
        e.stopPropagation();
        go(1);
      }} disabled={index === pages.length - 1} aria-label="Next page" className="absolute right-4 top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/20 disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-shuttle sm:flex" data-id="element-216" data-mp="s4si4g0j3r7a">
            <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-217" data-mp="1ficipj3p5kp3" />
          </button>

          <AnimatePresence initial={false} custom={direction} mode="popLayout" data-id="element-218" data-mp="1kfm4morvne86">
            <motion.img key={page.image} src={page.image} alt={page.alt} custom={direction} variants={{
          enter: (d: number) => ({
            opacity: 0,
            x: d * 48,
            scale: 0.98
          }),
          center: {
            opacity: 1,
            x: 0,
            scale: 1
          },
          exit: (d: number) => ({
            opacity: 0,
            x: d * -48,
            scale: 0.98
          })
        }} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.22,
          ease: EASE
        }} drag="x" dragConstraints={{
          left: 0,
          right: 0
        }} dragElastic={0.5} onDragEnd={onDragEnd} onClick={e => e.stopPropagation()} draggable={false} className="aspect-[9/16] cursor-grab touch-pan-y select-none rounded-2xl bg-white object-contain shadow-2xl active:cursor-grabbing" style={{
          height: 'min(84vh, calc(92vw * 16 / 9))'
        }} data-id="element-219" data-mp="1ohps6brtm99t" />
          </AnimatePresence>
        </motion.div>}
    </AnimatePresence>;
}